import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, ShieldCheck, Dna, ChevronRight,
  Mail, User, Loader2, Sparkles, Microscope, Camera, FlaskConical,
  Sprout, Atom, Salad, HeartPulse
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { invitationsAPI, testimonialsAPI } from '../lib/api';
import { pillars } from '../data/pillars';
import { SiteNav } from '../components/SiteNav';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { motion } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';
const HERO_IMAGE = 'https://images.unsplash.com/photo-1728727217834-b190862837a3?w=900&q=85&auto=format&fit=crop';

const defaultTestimonials = [
  {
    id: '1',
    name: 'Emma Richardson',
    role: 'Member, Fertility Journey',
    image_url: 'https://images.unsplash.com/photo-1745434159123-5b99b94206ca?w=100&h=100&fit=crop&crop=face',
    quote: 'Understanding my genotype and microbiome changed everything. Small, personalized shifts made me feel like myself again.',
  },
  {
    id: '2',
    name: 'Dr. Sarah Chen',
    role: 'Functional Nutrition Practitioner',
    image_url: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?w=100&h=100&fit=crop&crop=face',
    quote: 'This is where precision wellness is heading — the microbiome and gene expression, translated into everyday choices.',
  },
  {
    id: '3',
    name: 'Jessica Thompson',
    role: 'Member, Perimenopause',
    image_url: 'https://images.unsplash.com/photo-1745434159123-4908d0b9df94?w=100&h=100&fit=crop&crop=face',
    quote: 'Finally guidance built for my biology and my stage of life. My energy and sleep are in a completely different place.',
  },
];

const journeySteps = [
  { step: '01', title: 'Discover', desc: 'Map your genotype and microbiome signals through health history and biometric analysis.' },
  { step: '02', title: 'Personalize', desc: 'Receive guidance tuned to your biology, life stage and goals.' },
  { step: '03', title: 'Optimize', desc: 'Adjust nutrition, lifestyle and daily rhythms with clear, preventative steps.' },
  { step: '04', title: 'Thrive', desc: 'Feel the difference and evolve your plan as your body and season of life change.' },
];

const faqs = [
  {
    question: 'What is Rejuven?',
    answer: "Rejuven is a precision wellness platform for women that translates your genotype and microbiome signals into personalized, preventative guidance. We support you across life stages — fertility, general nutrition, early-adult wellness, menopause, longevity, hormone balance, peptide education and child nutrition.",
  },
  {
    question: 'How does Rejuven identify my genotype without lab tests?',
    answer: 'Rejuven combines a detailed health and family-history questionnaire, optional biometric analysis using your phone camera, and AI pattern-matching against validated profiles. This gives an educational picture of your likely genotype and microbiome tendencies without expensive laboratory testing.',
  },
  {
    question: 'What areas of wellness can Rejuven help with?',
    answer: 'Rejuven offers preventative wellness guidance across fertility, general nutrition, early-adult wellness, menopause and perimenopause, longevity, bioidentical hormone education, peptide insights and child nutrition — all personalized to your biology.',
  },
  {
    question: 'Does Rejuven prescribe peptides or hormones?',
    answer: 'No. Rejuven provides educational insights on peptides, hormones and how they may interact with your biology. We do not prescribe, diagnose or provide medical treatment. Always consult a licensed healthcare provider before starting any protocol.',
  },
  {
    question: 'Is Rejuven a substitute for medical care?',
    answer: 'No. Rejuven is for educational and preventative wellness purposes only and is not a substitute for professional medical advice, diagnosis or treatment. Our guidance focuses on nutrition, lifestyle and healthy habits — not pharmacological treatment.',
  },
  {
    question: 'Can Rejuven support me through menopause?',
    answer: 'Yes. Rejuven offers preventative, lifestyle-first guidance for perimenopause and menopause, tuned to your biology and stage of life — helping you support energy, sleep, mood and metabolic health through personalized nutrition and daily habits.',
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

export default function LandingPage() {
  const [searchParams] = useSearchParams();
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({ name: '', email: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonials, setTestimonials] = useState(defaultTestimonials);

  useEffect(() => {
    if (searchParams.get('waitlist') === 'true') setShowWaitlistModal(true);
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const response = await testimonialsAPI.getPublic();
        if (response.data && response.data.length > 0) setTestimonials(response.data);
      } catch (error) {
        console.log('Using default testimonials');
      }
    })();
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistForm.name || !waitlistForm.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    setSubmitting(true);
    try {
      await invitationsAPI.requestAccess(waitlistForm);
      setSubmitted(true);
      toast.success("You're on the waitlist!");
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F7] text-[#2B2625] overflow-x-hidden">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-[#2B2625] text-white px-4 py-2 rounded-full z-[100]">
        Skip to main content
      </a>

      {/* Navigation */}
      <SiteNav />

      {/* Hero */}
      <section id="main-content" className="pt-16 lg:pt-24 pb-14 lg:pb-28 px-6 lg:px-12 relative overflow-hidden" aria-labelledby="hero-heading">
        <div className="absolute -top-24 -right-24 w-[520px] h-[520px] bg-[#E8D5D1]/50 rounded-full blur-[130px]" />
        <div className="absolute top-40 -left-24 w-[400px] h-[400px] bg-[#899285]/15 rounded-full blur-[130px]" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div className="order-1" initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 border border-[#E8D5D1] rounded-full text-[#C28B87] text-sm font-semibold mb-6 lg:mb-8">
                <Sparkles className="w-4 h-4" /> Precision Wellness · Private Beta
              </motion.div>
              <motion.h1 id="hero-heading" variants={fadeInUp} className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#2B2625] leading-[1.02] mb-5 lg:mb-7 font-medium">
                Your biology, <span className="italic text-[#C28B87]">finally understood</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-base lg:text-xl text-[#6C6361] mb-7 lg:mb-9 max-w-xl leading-relaxed hero-description">
                A microbiome and genotype-based precision wellness platform built for women. From fertility and hormones to menopause and longevity — personalized, preventative guidance rooted in your unique biology.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
                <Button onClick={() => setShowWaitlistModal(true)} className="bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full px-8 py-6 lg:px-10 lg:py-7 text-base lg:text-lg font-medium shadow-[0_10px_30px_rgba(194,139,135,0.35)] transition-transform hover:scale-[1.02]" data-testid="hero-waitlist-btn">
                  Join the Waitlist <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <a href="#pillars">
                  <Button variant="outline" className="w-full sm:w-auto rounded-full px-8 py-6 lg:px-10 lg:py-7 text-base lg:text-lg border-[#E8D5D1] text-[#2B2625] hover:bg-[#F3EFEF]">Explore What We Support</Button>
                </a>
              </motion.div>
              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-6 text-sm text-[#6C6361]">
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#899285]" /> Evidence-informed</div>
                <div className="flex items-center gap-2"><Sprout className="w-4 h-4 text-[#899285]" /> Preventative, not pharmacological</div>
                <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C28B87]" /> Built for women</div>
              </motion.div>
            </motion.div>

            <motion.div className="order-2 hidden lg:block relative" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}>
              <div className="relative">
                <div className="relative aspect-[4/5] max-w-md mx-auto lg:ml-auto rounded-[3rem] overflow-hidden shadow-[0_40px_80px_-24px_rgba(43,38,37,0.25)]">
                  <img src={HERO_IMAGE} alt="A woman glowing with health, representing personalized precision wellness" className="w-full h-full object-cover" loading="eager" fetchPriority="high" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2B2625]/15 via-transparent to-transparent" />
                </div>
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.6 }} className="absolute -left-6 bottom-16 bg-white/90 backdrop-blur-xl p-5 rounded-2xl shadow-[0_20px_50px_-15px_rgba(43,38,37,0.15)] border border-[#E8D5D1]">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#F3EFEF] rounded-xl flex items-center justify-center"><Microscope className="w-6 h-6 text-[#C28B87]" /></div>
                    <div>
                      <p className="text-[#2B2625] font-semibold">Genotype + Microbiome</p>
                      <p className="text-[#6C6361] text-sm">No lab tests required</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Statement */}
      <section className="py-24 lg:py-32 bg-[#2B2625] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#C28B87] rounded-full blur-[160px]" />
          <div className="absolute bottom-0 right-1/4 w-[420px] h-[420px] bg-[#899285] rounded-full blur-[160px] opacity-40" />
        </div>
        <motion.div className="max-w-4xl mx-auto px-6 text-center relative z-10" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-[#E8D5D1] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-7">Wellness, personalized to you</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-8 font-medium">
            You are not average — so why is your wellness plan?
          </h2>
          <p className="text-lg lg:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
            Your genes set the blueprint, but your microbiome, lifestyle and environment shape how that story unfolds. Rejuven reads those signals and turns them into simple, preventative steps designed for your body and your stage of life.
          </p>
        </motion.div>
      </section>

      {/* Wellness Pillars */}
      <section id="pillars" className="py-24 lg:py-32 px-6 lg:px-12 bg-[#FAF8F7]">
        <div className="max-w-7xl mx-auto">
          <motion.div className="mb-16 max-w-2xl" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">What We Support</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[#2B2625] mb-5 font-medium">One platform, every season of a woman&apos;s life</h2>
            <p className="text-lg text-[#6C6361] leading-relaxed">Personalized, preventative guidance across the areas that matter most — from your first cycle to graceful longevity.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, idx) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (idx % 4) * 0.08, duration: 0.5 }}
              >
                <Link
                  to={`/pillars/${p.slug}`}
                  className="group flex flex-col h-full bg-white rounded-3xl p-8 border border-[#E8D5D1]/70 shadow-[0_8px_32px_rgba(43,38,37,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_44px_-18px_rgba(43,38,37,0.16)] transition-[transform,box-shadow] duration-300"
                  data-testid={`pillar-${p.slug}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#F3EFEF] flex items-center justify-center mb-6 group-hover:bg-[#E8D5D1] transition-colors duration-300">
                    <p.icon className="w-7 h-7 text-[#C28B87]" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display text-2xl text-[#2B2625] mb-2 font-medium">{p.title}</h3>
                  <p className="text-[#6C6361] text-sm leading-relaxed mb-4">{p.shortDesc}</p>
                  <span className="mt-auto inline-flex items-center gap-1 text-sm text-[#C28B87] font-medium group-hover:gap-2 transition-all">Explore <ChevronRight className="w-4 h-4" /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Bento */}
      <section id="how-it-works" className="py-24 lg:py-32 px-6 lg:px-12 bg-gradient-to-b from-[#FAF8F7] via-[#F3EFEF]/50 to-[#FAF8F7]">
        <div className="max-w-7xl mx-auto">
          <motion.div className="mb-16 max-w-2xl" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">The Science</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[#2B2625] mb-5 font-medium">Reading your biology without the lab</h2>
            <p className="text-lg text-[#6C6361] leading-relaxed">We combine what you know about your body with what your biology reveals — then translate it into guidance you can actually use.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div className="md:col-span-2 bg-white rounded-[2rem] p-10 lg:p-12 border border-[#E8D5D1]/70 shadow-[0_8px_32px_rgba(43,38,37,0.04)]" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 }}>
              <div className="w-16 h-16 rounded-2xl bg-[#F3EFEF] flex items-center justify-center mb-8"><Dna className="w-8 h-8 text-[#C28B87]" strokeWidth={1.6} /></div>
              <h3 className="font-display text-3xl text-[#2B2625] mb-4 font-medium">Genotype & microbiome signals</h3>
              <p className="text-[#6C6361] text-lg leading-relaxed max-w-lg mb-6">
                No expensive testing required. We blend a detailed health and family history, <strong className="text-[#2B2625] font-semibold">optional biometric analysis using your phone camera</strong>, and AI pattern-matching to build an educational picture of your likely genotype and gut tendencies.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F3EFEF] rounded-full text-sm text-[#6C6361]"><Camera className="w-4 h-4 text-[#899285]" /> Camera Analysis</span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F3EFEF] rounded-full text-sm text-[#6C6361]"><Microscope className="w-4 h-4 text-[#899285]" /> Microbiome Signals</span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F3EFEF] rounded-full text-sm text-[#6C6361]"><Atom className="w-4 h-4 text-[#899285]" /> AI Matching</span>
              </div>
            </motion.div>

            <motion.div className="bg-[#E8D5D1]/50 rounded-[2rem] p-10 border border-[#E8D5D1]" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center mb-8"><Salad className="w-8 h-8 text-[#C28B87]" strokeWidth={1.6} /></div>
              <h3 className="font-display text-2xl text-[#2B2625] mb-4 font-medium">Personalized nutrition</h3>
              <p className="text-[#6C6361] leading-relaxed">Everyday food and lifestyle guidance mapped to your biology and goals — never generic rules.</p>
            </motion.div>

            <motion.div className="bg-white rounded-[2rem] p-10 border border-[#E8D5D1]/70 shadow-[0_8px_32px_rgba(43,38,37,0.04)]" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <div className="w-16 h-16 rounded-2xl bg-[#F3EFEF] flex items-center justify-center mb-8"><FlaskConical className="w-8 h-8 text-[#C28B87]" strokeWidth={1.6} /></div>
              <h3 className="font-display text-2xl text-[#2B2625] mb-4 font-medium">Peptide & hormone education</h3>
              <p className="text-[#6C6361] leading-relaxed">Clear, unbiased insight into peptides, hormones and how they may interact with your biology — for understanding, not prescription.</p>
            </motion.div>

            <motion.div className="md:col-span-2 bg-[#2B2625] rounded-[2rem] p-10 lg:p-12 text-white" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.25 }}>
              <div className="flex flex-col lg:flex-row gap-8 items-start">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0"><HeartPulse className="w-8 h-8 text-[#E8D5D1]" strokeWidth={1.6} /></div>
                <div>
                  <h3 className="font-display text-3xl mb-4 font-medium">Preventative, life-stage aware</h3>
                  <p className="text-white/65 text-lg leading-relaxed">Whether you&apos;re planning a family, navigating menopause, or investing in longevity, your guidance evolves with your body — focused on prevention and healthy habits, not pharmacological treatment.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 bg-[#FAF8F7]">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">Your Journey</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[#2B2625] font-medium">From discovery to thriving</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {journeySteps.map((item, idx) => (
              <motion.div key={idx} className="relative group" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}>
                <div className="bg-white rounded-[2rem] p-9 h-full border border-[#E8D5D1]/70 hover:border-[#C28B87]/40 hover:shadow-[0_20px_44px_-18px_rgba(43,38,37,0.14)] transition-[border-color,box-shadow] duration-300">
                  <span className="font-display text-6xl text-[#E8D5D1] leading-none">{item.step}</span>
                  <h3 className="font-display text-2xl text-[#2B2625] mt-5 mb-3 font-medium">{item.title}</h3>
                  <p className="text-[#6C6361] text-sm leading-relaxed">{item.desc}</p>
                </div>
                {idx < journeySteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-6 text-[#C28B87]/40 -translate-y-1/2"><ChevronRight className="w-6 h-6" /></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section id="stories" className="py-24 lg:py-32 px-6 lg:px-12 bg-gradient-to-b from-[#FAF8F7] via-[#F3EFEF]/40 to-[#FAF8F7]">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">Stories</p>
              <h2 className="font-display text-4xl sm:text-5xl text-[#2B2625] font-medium">Women who found their footing</h2>
            </motion.div>
            <motion.div className="relative" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="bg-white rounded-[2.5rem] p-10 lg:p-16 border border-[#E8D5D1]/70 shadow-[0_8px_32px_rgba(43,38,37,0.05)]">
                <div className="flex flex-col lg:flex-row items-center gap-10">
                  <img src={testimonials[activeTestimonial]?.image_url} alt={testimonials[activeTestimonial]?.name} className="w-28 h-28 rounded-full object-cover border-4 border-[#F3EFEF] shadow-lg" />
                  <div className="flex-1 text-center lg:text-left">
                    <svg className="w-11 h-11 text-[#E8D5D1] mb-6 mx-auto lg:mx-0" fill="currentColor" viewBox="0 0 24 24"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" /></svg>
                    <p className="font-display text-2xl lg:text-3xl text-[#2B2625] mb-8 leading-relaxed italic">&ldquo;{testimonials[activeTestimonial]?.quote}&rdquo;</p>
                    <p className="font-semibold text-[#2B2625] text-lg">{testimonials[activeTestimonial]?.name}</p>
                    <p className="text-[#6C6361]">{testimonials[activeTestimonial]?.role}</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center gap-3 mt-10">
                {testimonials.map((_, idx) => (
                  <button key={idx} onClick={() => setActiveTestimonial(idx)} className={`h-2 rounded-full transition-all duration-300 ${idx === activeTestimonial ? 'w-10 bg-[#C28B87]' : 'w-2 bg-[#E8D5D1] hover:bg-[#C28B87]/50'}`} data-testid={`testimonial-dot-${idx}`} aria-label={`Show testimonial ${idx + 1}`} />
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Founder band */}
      <section className="py-20 lg:py-24 px-6 lg:px-12 bg-[#FAF8F7]">
        <div className="max-w-5xl mx-auto bg-white rounded-[2.5rem] p-8 lg:p-12 border border-[#E8D5D1]/70 shadow-[0_8px_32px_rgba(43,38,37,0.05)]">
          <div className="flex flex-col md:flex-row items-center gap-8 lg:gap-12">
            <img src="https://customer-assets.emergentagent.com/job_rejuven-fertility/artifacts/n9q77zae_Erika%20Portrait.jpg" alt="Dr. Erika Tajra, Founder of Rejuven" className="w-32 h-32 lg:w-40 lg:h-40 rounded-full object-cover border-4 border-[#F3EFEF] flex-shrink-0" />
            <div className="text-center md:text-left">
              <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-3">Guided by our founder</p>
              <h2 className="font-display text-3xl lg:text-4xl text-[#2B2625] mb-4 font-medium">Science, translated with warmth</h2>
              <p className="text-[#6C6361] leading-relaxed mb-5">
                Rejuven is founded by Dr. Erika Tajra — a medical doctor and epigenetics researcher whose own journey sparked a mission to make precision wellness accessible to women everywhere. Her philosophy: understand the biology, then empower everyday choices.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-[#2B2625] font-medium hover:text-[#C28B87] transition-colors">Read her story <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 lg:py-32 px-6 lg:px-12 bg-gradient-to-b from-[#FAF8F7] to-[#F3EFEF]/40">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-14" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">Common Questions</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[#2B2625] mb-5 font-medium">Frequently asked questions</h2>
            <p className="text-lg text-[#6C6361] leading-relaxed max-w-2xl mx-auto">Everything you need to know about precision wellness, the microbiome, genotype insights and how Rejuven works.</p>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <motion.div key={idx} className="bg-white rounded-2xl border border-[#E8D5D1]/70 overflow-hidden" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.05 }}>
                <details className="group">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none" data-testid={`faq-item-${idx}`}>
                    <h3 className="text-lg font-medium text-[#2B2625] pr-4">{faq.question}</h3>
                    <ChevronRight className="w-5 h-5 text-[#C28B87] transition-transform duration-300 group-open:rotate-90 flex-shrink-0" />
                  </summary>
                  <div className="px-6 pb-6 text-[#6C6361] leading-relaxed faq-answer"><p>{faq.answer}</p></div>
                </details>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-[#6C6361] mb-3">Have more questions?</p>
            <Link to="/contact" className="text-[#2B2625] font-medium hover:text-[#C28B87] transition-colors">Contact our team →</Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 bg-[#2B2625] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[#C28B87]/25 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#899285]/20 rounded-full blur-[120px]" />
        </div>
        <motion.div className="max-w-3xl mx-auto text-center relative z-10" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="text-[#E8D5D1] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-7">Private Beta</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-7 leading-[1.05] font-medium">Be first to experience precision wellness</h2>
          <p className="text-lg lg:text-xl text-white/65 mb-11 max-w-2xl mx-auto leading-relaxed">Join our private beta waitlist and get early access to personalized, preventative guidance built for your biology.</p>
          <Button onClick={() => setShowWaitlistModal(true)} className="bg-white text-[#2B2625] hover:bg-[#F3EFEF] rounded-full px-12 py-7 text-lg font-semibold shadow-[0_10px_30px_rgba(255,255,255,0.15)] transition-transform hover:scale-[1.02]" data-testid="footer-cta-btn">
            Join the Waitlist <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-white/40 text-sm mt-7">Limited spots · Founding member benefits</p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-[#221D1C] text-white py-16 px-6" role="contentinfo">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-10">
            <div className="bg-white/10 rounded-2xl px-6 py-3"><img src={LOGO_URL} alt="Rejuven - Precision Wellness Platform" className="h-10" /></div>
            <nav aria-label="Footer navigation" className="flex flex-wrap justify-center gap-10 text-sm text-white/55">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </nav>
            <p className="text-white/35 text-sm">© 2026 Rejuven Inc.</p>
          </div>
          <div className="border-t border-white/10 pt-8">
            <p className="text-white/45 text-xs leading-relaxed max-w-4xl mx-auto text-center">
              <strong className="text-white/60">Medical disclaimer:</strong> Rejuven provides educational and preventative wellness information only. It is not medical advice and is not a substitute for professional diagnosis, treatment or the advice of your licensed physician. We do not diagnose, treat, cure or prevent any disease, and we do not prescribe medications, hormones or peptides. Always consult a qualified healthcare provider before making changes to your health regimen.
            </p>
          </div>
        </div>
      </footer>

      {/* Waitlist Modal */}
      <Dialog open={showWaitlistModal} onOpenChange={setShowWaitlistModal}>
        <DialogContent className="max-w-md bg-white border border-[#E8D5D1] rounded-3xl shadow-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-center text-[#2B2625] font-medium">{submitted ? "You're on the Waitlist!" : 'Join the Private Beta'}</DialogTitle>
            <DialogDescription className="text-center text-[#6C6361]">
              {submitted ? "We'll notify you as soon as spots open up." : 'Be among the first to access precision wellness built for your biology.'}
            </DialogDescription>
          </DialogHeader>
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-[#F3EFEF] rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-[#C28B87]" /></div>
              <p className="text-[#6C6361] mb-8">We&apos;ll email you when it&apos;s your turn to join. Keep an eye on your inbox!</p>
              <Button onClick={() => { setShowWaitlistModal(false); setSubmitted(false); setWaitlistForm({ name: '', email: '', reason: '' }); }} variant="outline" className="w-full rounded-full border-[#E8D5D1] text-[#2B2625]">Close</Button>
            </div>
          ) : (
            <form onSubmit={handleJoinWaitlist} className="space-y-5 pt-4">
              <div className="space-y-2">
                <Label htmlFor="waitlist-name" className="text-[#2B2625]">Your Name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C28B87]" />
                  <Input id="waitlist-name" placeholder="Your full name" value={waitlistForm.name} onChange={(e) => setWaitlistForm(p => ({ ...p, name: e.target.value }))} className="pl-12 rounded-xl border-[#E8D5D1] focus:border-[#C28B87] bg-[#FAF8F7]" required data-testid="waitlist-name-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="waitlist-email" className="text-[#2B2625]">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C28B87]" />
                  <Input id="waitlist-email" type="email" placeholder="you@example.com" value={waitlistForm.email} onChange={(e) => setWaitlistForm(p => ({ ...p, email: e.target.value }))} className="pl-12 rounded-xl border-[#E8D5D1] focus:border-[#C28B87] bg-[#FAF8F7]" required data-testid="waitlist-email-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="waitlist-reason" className="text-[#2B2625]">What brings you to Rejuven? <span className="text-[#6C6361]">(optional)</span></Label>
                <Textarea id="waitlist-reason" placeholder="Fertility, hormones, menopause, longevity, nutrition..." value={waitlistForm.reason} onChange={(e) => setWaitlistForm(p => ({ ...p, reason: e.target.value }))} rows={3} className="rounded-xl border-[#E8D5D1] focus:border-[#C28B87] bg-[#FAF8F7]" data-testid="waitlist-reason-input" />
              </div>
              <div className="bg-[#F3EFEF] rounded-2xl p-5 text-sm text-[#6C6361]">
                <div className="flex items-start gap-4">
                  <Sparkles className="w-5 h-5 text-[#C28B87] flex-shrink-0 mt-0.5" />
                  <div><span className="font-semibold text-[#2B2625]">Exclusive Beta Access</span><br />Waitlist members get priority access and founding-member benefits.</div>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full py-6 shadow-lg" data-testid="waitlist-submit-btn">
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Joining...</>) : (<>Join the Waitlist <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
